import React, { useState, useEffect, useContext } from 'react';
import { Container, Grid, TextField, Checkbox, InputAdornment } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DealStage, DealType, DealSource, DealReasonForLoss } from '../../../utils/enums'
import UsersAutoComplete from '../../Users/UsersAutoComplete';
import dayjs from 'dayjs';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AccountsAutoComplete from '../../Accounts/AccountsAutoComplete';
import ContactsAutoComplete from '../../Contacts/ContactsAutoComplete';
import CampaignsAutoComplete from '../../Campaigns/CampaignsAutoComplete';
import { AuthContext } from '../../../context/AuthContext';

const CreateForm = ({ onSubmit, contactIdPar }) => {
    const { userId } = useContext(AuthContext);
    const [ownerId, setOwnerId] = useState(userId || '');
    const [dealName, setDealName] = useState('');
    const [country, setCountry] = useState('');
    const [accountId, setAccountId] = useState(0);
    const [amount, setAmount] = useState(null);
    const [closingDate, setClosingDate] = useState(dayjs());
    const [stage, setStage] = useState(0);
    const [reasonForLoss, setReasonForLoss] = useState(0);
    const [probability, setProbability] = useState(10);
    const [type, setType] = useState(0);
    const [expectedRevenue, setExpectedRevenue] = useState(0);
    const [leadSource, setLeadSource] = useState(0);
    const [contactId, setContactId] = useState(null);
    const [nextStep, setNextStep] = useState('');
    const [campaignId, setCampaignId] = useState(null);
    const [campaignSource, setCampaignSource] = useState('');
    const [description, setDescription] = useState('');
    const [shouldFetchSingleContact, setShouldFetchSingleContact] = useState(false);

    useEffect(() => {
        if (contactIdPar) {
            setContactId(contactIdPar);
            setShouldFetchSingleContact(true);
        }
    }, [contactIdPar]);

    useEffect(() => {
        if (userId) {
            setOwnerId(userId);
        }
    }, [userId]);


    const handleSubmit = (e) => {
        e.preventDefault();
        // Collect all form data
        const formData = {
            ownerId,
            dealName,
            country,
            accountId,
            amount,
            closingDate,
            stage,
            reasonForLoss,
            probability,
            type,
            expectedRevenue,
            leadSource,
            contactId,
            nextStep,
            campaignId,
            campaignSource,
            description
        };
        // Call onSubmit function passed as a prop
        onSubmit(formData);
    };

    const handleStageChange = (newValue) => {
        setStage(newValue ? newValue.value : '');
        const selectedStage = DealStage.find((option) => option.value === newValue?.value);

        if (selectedStage) {
            setProbability(selectedStage.probability); // Update probability as percentage
            calculateExpectedRevenue(amount, selectedStage.probability);
        }
    };

    const handleAmountChange = (e) => {
        const newAmount = e.target.value;
        setAmount(newAmount);
        const selectedStage = DealStage.find((option) => option.value === stage);

        if (selectedStage) {
            calculateExpectedRevenue(newAmount, selectedStage.probability);
        }
    };

    const calculateExpectedRevenue = (amount, probability) => {
        if (!amount || isNaN(amount)) return setExpectedRevenue(0);

        setExpectedRevenue(amount * (probability / 100));
    };

    return (
        <form id='create-form' onSubmit={handleSubmit}>
            <Container maxWidth="md" sx={{ marginTop: "10px" }}>
                <h4>Deal Information</h4>
                <Grid container spacing={3}>
                    {/* Deal Owner */}
                    <Grid item xs={12} sm={6}>
                        <UsersAutoComplete userId={userId} setUserId={setOwnerId} />
                    </Grid>

                    {/* Amount ****** */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Amount"
                            value={amount}
                            onChange={handleAmountChange}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                    </Grid>

                    {/* Deal Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Deal Name" required
                            onChange={(e) => setDealName(e.target.value)} />
                    </Grid>

                    {/* Country */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Country"
                            onChange={(e) => setCountry(e.target.value)} />
                    </Grid>

                    {/* Closing Date */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Closing Date"
                                fullWidth
                                required
                                value={closingDate}
                                onChange={(newDate) => setClosingDate(newDate)}
                                slotProps={{
                                    textField: {
                                        required: 'true',
                                        variant: 'outlined',
                                        fullWidth: true
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Account Name */}
                    <Grid item xs={12} sm={6}>
                        {/* <TextField fullWidth label="Account Name" required
                            onChange={(e) => setAccountId(e.target.value)} /> */}
                        <AccountsAutoComplete required={true} accountId={accountId} setAccountId={setAccountId} />
                    </Grid>

                    {/* Stage */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            required
                            id="combo-box-demo"
                            options={DealStage}
                            value={DealStage.find((option) => option.value === 0)}
                            sx={{ width: "100%" }}
                            onChange={(event, newValue) => handleStageChange(newValue)}
                            renderInput={(params) => <TextField {...params} label="Stage" />}
                        />
                    </Grid>

                    {/* if stage is deal lost */}
                    {(stage === 7 || stage === 8) && (
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                disablePortal
                                required
                                id="dealReasonForLoss"
                                options={DealReasonForLoss}
                                value={DealReasonForLoss.find((option) => option.value === 0)}
                                sx={{ width: "100%" }}
                                onChange={(event, newValue) => setReasonForLoss(newValue ? newValue.value : 0)}
                                renderInput={(params) => <TextField {...params} label="Deal Reason for Loss" />}
                            />
                        </Grid>
                    )}

                    {/* Type */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={DealType}
                            sx={{ width: "100%" }}
                            value={DealType.find((option) => option.value === 0)}
                            onChange={(event, newValue) => setType(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Type" />}
                        />
                    </Grid>

                    {/* Probability */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Probability (%)"
                            value={probability}
                            onChange={(e) => setProbability(e.target.value)} />
                    </Grid>

                    {/* Expected Revenue */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Expected Revenue"
                            disabled
                            value={expectedRevenue}
                            onChange={(e) => setExpectedRevenue(e.target.value)} />
                    </Grid>

                    {/* Next Step */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Next Step"
                            onChange={(e) => setNextStep(e.target.value)} />
                    </Grid>

                    {/* Deal Source */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={DealSource}
                            sx={{ width: "100%" }}
                            value={DealSource.find((option) => option.value === 0)}
                            onChange={(event, newValue) => setLeadSource(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Lead Source" />}
                        />
                    </Grid>

                    {/* Campaign Source */}
                    <Grid item xs={12} sm={6}>
                        {/* <TextField fullWidth label="Campaign Source"
                            onChange={(e) => setCampaignSource(e.target.value)} /> */}
                        <CampaignsAutoComplete campaignId={campaignId} setCampaignId={setCampaignId} shouldFetchSingleCampaign={false} />
                    </Grid>

                    {/* Contact Name */}
                    <Grid item xs={12} sm={6}>
                        <ContactsAutoComplete contactId={contactId} setContactId={setContactId} shouldFetchSingleContact={shouldFetchSingleContact} />
                    </Grid>

                </Grid>
                <br />
                <h4>Description Information</h4>
                <Grid container spacing={3}>
                    {/* Description */}
                    <Grid item xs={12} sm={12}>
                        <TextField multiline fullWidth label="Description"
                            onChange={(e) => setDescription(e.target.value)} />
                    </Grid>
                </Grid >
            </Container >
        </form>
    );
};

export default CreateForm;
